import type { BrickId, PlacedBrick, WallModel } from '@bw/core';
import type { Viewport } from './viewport.js';
import { VIEWPORT_OVERSCAN_PX } from './viewport.js';
import type { DrawTheme } from './theme.js';
import { darken, lighten } from './theme.js';

/** SPECIFICATION.md clause 11.6 — level of detail. */
export const BRICK_DETAIL_LIMIT = 1000;
export const BRICK_DETAIL_MIN_PX = 14;

export interface WallPaintOptions {
  readonly model: WallModel;
  readonly viewport: Viewport;
  readonly theme: DrawTheme;
  /** brick.materialId -> hex colour. */
  readonly colourOf: (brick: PlacedBrick) => string;
  readonly selected: ReadonlySet<BrickId>;
  /** Bricks being dragged, drawn faint in place (design document C3). */
  readonly dimmed?: ReadonlySet<BrickId> | undefined;
}

export interface WallPaintStats {
  readonly visible: number;
  readonly detailed: boolean;
}

/**
 * Paint the laid wall. Culled to the viewport plus a 40 px overscan, batched
 * by colour so one `fillStyle` assignment serves every brick of that material
 * (design document B4).
 */
export function paintWall(
  ctx: CanvasRenderingContext2D,
  options: WallPaintOptions,
): WallPaintStats {
  const { model, viewport, theme, colourOf, selected, dimmed } = options;
  const view = viewport.visibleBounds(VIEWPORT_OVERSCAN_PX);
  const visible = model.bricksInRect(view.minX, view.minY, view.maxX, view.maxY);
  const detailed = visible.length <= BRICK_DETAIL_LIMIT;
  const scale = viewport.scale;

  // Group by colour so fills batch; selection and dimming are drawn after.
  const byColour = new Map<string, PlacedBrick[]>();
  for (const brick of visible) {
    const colour = colourOf(brick);
    const bucket = byColour.get(colour);
    if (bucket === undefined) byColour.set(colour, [brick]);
    else bucket.push(brick);
  }

  const outlineWidth = Math.max(1, Math.round(scale * 1.5));

  for (const [colour, bricks] of byColour) {
    // Bodies.
    ctx.fillStyle = colour;
    ctx.beginPath();
    for (const brick of bricks) {
      const left = viewport.toScreenX(brick.x);
      const top = viewport.toScreenY(brick.top);
      ctx.rect(left, top, brick.width * scale, brick.height * scale);
    }
    ctx.fill();

    // Unselected outlines, in one stroke per colour.
    ctx.strokeStyle = darken(colour, 0.48);
    ctx.lineWidth = outlineWidth;
    ctx.beginPath();
    for (const brick of bricks) {
      if (selected.has(brick.id)) continue;
      const left = viewport.toScreenX(brick.x);
      const top = viewport.toScreenY(brick.top);
      ctx.rect(left, top, brick.width * scale, brick.height * scale);
    }
    ctx.stroke();

    if (!detailed) continue;

    // Bevel: highlight up the left and across the top, shadow down the right
    // and back along the bottom (clause 12.1).
    const inset = Math.min(3, Math.max(1, scale * 2));
    ctx.lineWidth = 1;
    ctx.strokeStyle = lighten(colour, 0.18);
    ctx.beginPath();
    let anyBevel = false;
    for (const brick of bricks) {
      const w = brick.width * scale;
      const h = brick.height * scale;
      if (w < BRICK_DETAIL_MIN_PX || h < BRICK_DETAIL_MIN_PX) continue;
      anyBevel = true;
      const left = viewport.toScreenX(brick.x);
      const top = viewport.toScreenY(brick.top);
      ctx.moveTo(left + inset, top + h - inset);
      ctx.lineTo(left + inset, top + inset);
      ctx.lineTo(left + w - inset, top + inset);
    }
    if (anyBevel) ctx.stroke();

    ctx.strokeStyle = darken(colour, 0.18);
    ctx.beginPath();
    for (const brick of bricks) {
      const w = brick.width * scale;
      const h = brick.height * scale;
      if (w < BRICK_DETAIL_MIN_PX || h < BRICK_DETAIL_MIN_PX) continue;
      const left = viewport.toScreenX(brick.x);
      const top = viewport.toScreenY(brick.top);
      ctx.moveTo(left + w - inset, top + inset);
      ctx.lineTo(left + w - inset, top + h - inset);
      ctx.lineTo(left + inset, top + h - inset);
    }
    if (anyBevel) ctx.stroke();
  }

  // Selection outlines on top, so they are never overdrawn by a neighbour.
  if (selected.size > 0) {
    ctx.strokeStyle = theme.selectionOutline;
    ctx.lineWidth = 3;
    ctx.beginPath();
    for (const brick of visible) {
      if (!selected.has(brick.id)) continue;
      const left = viewport.toScreenX(brick.x);
      const top = viewport.toScreenY(brick.top);
      ctx.rect(left, top, brick.width * scale, brick.height * scale);
    }
    ctx.stroke();
  }

  // Bricks being dragged read as "left behind" (design document C3).
  if (dimmed !== undefined && dimmed.size > 0) {
    ctx.save();
    ctx.globalAlpha = 0.65;
    ctx.fillStyle = theme.ghostFill;
    ctx.beginPath();
    for (const brick of visible) {
      if (!dimmed.has(brick.id)) continue;
      const left = viewport.toScreenX(brick.x);
      const top = viewport.toScreenY(brick.top);
      ctx.rect(left, top, brick.width * scale, brick.height * scale);
    }
    ctx.fill();
    ctx.restore();
  }

  return { visible: visible.length, detailed };
}

/**
 * Centred two-line prompt on an empty wall (clause 12.7).
 *
 * Both lines are laid out to the canvas width rather than drawn blind: on a
 * narrow window the long form ran off both edges and the keys line was cut
 * mid-word, which reads as a rendering fault rather than a hint. Each line
 * falls back to a shorter wording and is dropped entirely if even that will
 * not fit.
 */
export function paintEmptyHint(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  theme: DrawTheme,
): void {
  const centre = viewport.width / 2;
  const room = viewport.width - 24;
  ctx.save();
  ctx.textAlign = 'center';

  ctx.fillStyle = theme.hintStrong;
  ctx.font = 'bold 15px system-ui, sans-serif';
  const lead = fitText(ctx, room, [
    'Choose a brick, move it into position, then click to lay',
    'Choose a brick, then click to lay',
    'Click to lay a brick',
  ]);
  if (lead !== null) ctx.fillText(lead, centre, 64);

  ctx.fillStyle = theme.hintSoft;
  ctx.font = '12px system-ui, sans-serif';
  const keys = fitText(ctx, room, [
    'P: pan  ·  M: measure  ·  Wheel: zoom  ·  Space+drag: temporary pan  ·  R: rotate',
    'P: pan  ·  M: measure  ·  Wheel: zoom  ·  R: rotate',
    'Wheel: zoom  ·  R: rotate',
  ]);
  if (keys !== null) ctx.fillText(keys, centre, lead === null ? 64 : 88);

  ctx.restore();
}

/** The first candidate that measures within `room`, or null if none does. */
function fitText(
  ctx: CanvasRenderingContext2D,
  room: number,
  candidates: readonly string[],
): string | null {
  for (const candidate of candidates) {
    if (ctx.measureText(candidate).width <= room) return candidate;
  }
  return null;
}
